/**
 *
 *  Organizations/Show/LandingPagePreferences
 *
 */

import React, { useEffect, useState } from 'react'

// NPM Libraries
import AnimateHeight from 'react-animate-height'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// GraphQL
import useQuery from 'components/UseQuery'
import { gql, useMutation } from '@apollo/client'

// Components
import ContactRegistrationFields from 'components/LandingPagePreferences/ContactRegistrationFields'
import DropDownMenu from 'components/DropDownMenu'
import Modal from 'components/Modal'
import OrganizationOptions from './OrganizationOptions'

// Utils
import { COLORS } from 'global-styles'
import { useStateFromQuery, useHandleInputChange } from 'hooks'

const GET_ORGANIZATION = gql`
	query organization($id: ID) {
		organization(id: $id) {
			id
			social_share
			confirm_digital_registrations

			landing_page_configs {
				id
				objective_to_s
			}
		}
	}
`

const GET_LANDING_PAGE_CONFIG = gql`
	query landingPageConfig($id: ID!) {
		landing_page_config(id: $id) {
			id
			organization_id
			objective
			objective_to_s
			email_field
			phone_field
			address_field
			note_field
			terms_and_conditions_field
			follow_up_request_field
			disable_allow_appointments
			has_default_state
		}
	}
`

const UPDATE_LANDING_PAGE_CONFIG = gql`
	mutation UpdateLandingPageConfig(
		$id: ID!
		$email_field: Int
		$phone_field: Int
		$address_field: Int
		$note_field: Int
		$terms_and_conditions_field: Int
		$follow_up_request_field: Int
		$disable_allow_appointments: Boolean
		$reset_values: Boolean
	) {
		updateLandingPageConfig(
			input: {
				id: $id
				email_field: $email_field
				phone_field: $phone_field
				address_field: $address_field
				note_field: $note_field
				terms_and_conditions_field: $terms_and_conditions_field
				follow_up_request_field: $follow_up_request_field
				disable_allow_appointments: $disable_allow_appointments
				reset_values: $reset_values
			}
		) {
			landing_page_config {
				id
			}
		}
	}
`

const UPDATE_ORGANIZATION = gql`
	mutation updateOrganization(
		$id: ID!
		$default_campaign_questions: [String]
		$default_campaign_questions_required: Boolean
		$social_share: Boolean
		$confirm_digital_registrations: Boolean
	) {
		updateOrganization(
			input: {
				id: $id
				default_campaign_questions: $default_campaign_questions
				default_campaign_questions_required: $default_campaign_questions_required
				social_share: $social_share
				confirm_digital_registrations: $confirm_digital_registrations
			}
		) {
			organization {
				id
				name
			}
		}
	}
`

const Header = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	background-color: ${COLORS.borderGrey};
	padding: 10px 15px;
	margin-bottom: 5px;

	span {
		font-size: 1.2rem;
	}
`

const CheckIcon = styled(FontAwesomeIcon)`
	transition: all 0.5s;
`

function Section({ title, icon, is_open, toggleSection, children }) {
	return (
		<React.Fragment>
			<Header onClick={toggleSection} className="clickable">
				<div>
					<FontAwesomeIcon className="mr-2" icon={icon} />
					<span>{title}</span>
				</div>

				<FontAwesomeIcon className="text-primary" icon={is_open ? 'caret-circle-up' : 'caret-circle-down'} />
			</Header>

			<AnimateHeight height={is_open ? 'auto' : 0} duration={100}>
				<div className="border my-2">{children}</div>
			</AnimateHeight>
		</React.Fragment>
	)
}

function LandingPagePreferences({ organization_id, handleClose }) {
	const [section_open, setSectionOpen] = useState(null)
	const [selected, setSelected] = useState(null)
	const [completed, setCompleted] = useState(false)
	const [organization_state, , mergeOrganziationState] = useHandleInputChange({})
	const [has_changes, setHasChanges] = useState(false)

	const {
		data: { organization },
		loading: fetching_organization,
	} = useQuery(GET_ORGANIZATION, {
		variables: { id: organization_id },
		channels: [organization_id],
		skip: !organization_id,
		fetchPolicy: 'network-only',
	})

	const {
		state: config_state,
		mergeState: mergeConfigState,
		handleInputChange: handleConfigInputChange,
		data: { landing_page_config },
		loading: fetching_landing_page_config,
	} = useStateFromQuery(
		GET_LANDING_PAGE_CONFIG,
		{
			variables: { id: selected },
			channels: [selected],
			default_data: {
				landing_page_config: {
					modules: [],
					has_default_state: true,
				},
			},
			fetchPolicy: 'network-only',
		},
		'landing_page_config',
	)

	const [saveOrganization, { loading: saving_organization }] = useMutation(UPDATE_ORGANIZATION, {
		variables: {
			id: organization_id,
			...organization_state,
		},
		onCompleted: () => setCompleted(true),
	})

	const [saveLandingPageConfig, { loading: saving_landing_page_config }] = useMutation(UPDATE_LANDING_PAGE_CONFIG, {
		variables: {
			id: selected,
			...config_state,
			email_field: Number(config_state.email_field),
			phone_field: Number(config_state.phone_field),
			address_field: Number(config_state.address_field),
			note_field: Number(config_state.note_field),
			terms_and_conditions_field: Number(config_state.terms_and_conditions_field),
			follow_up_request_field: Number(config_state.follow_up_request_field),
		},
		onCompleted: () => setCompleted(true),
	})

	useEffect(() => {
		if (!!organization?.id) {
			const { id, landing_page_configs, ...rest } = organization

			mergeOrganziationState(rest)

			if (landing_page_configs?.length > 0) setSelected(landing_page_configs[0].id)
		}
	}, [organization])

	// set has_changes
	useEffect(() => {
		if (landing_page_config?.id) {
			const lpc = { ...landing_page_config }
			// eslint-disable-next-line no-shadow
			const has_changes_to_config =
				lpc.email_field !== config_state.email_field ||
				lpc.phone_field !== config_state.phone_field ||
				lpc.address_field !== config_state.address_field ||
				lpc.terms_and_conditions_field !== config_state.terms_and_conditions_field ||
				lpc.follow_up_request_field !== config_state.follow_up_request_field ||
				lpc.note_field !== config_state.note_field ||
				lpc.disable_allow_appointments !== config_state.disable_allow_appointments

			const org = { ...organization }
			// eslint-disable-next-line no-shadow
			const has_changes_to_organization =
				org.confirm_digital_registrations !== organization_state.confirm_digital_registrations ||
				org.social_share !== organization_state.social_share ||
				org.default_campaign_questions_required !== organization_state.default_campaign_questions_required

			setHasChanges(has_changes_to_config || has_changes_to_organization)
		}
	}, [
		JSON.stringify(landing_page_config),
		JSON.stringify(config_state),
		JSON.stringify(organization),
		JSON.stringify(organization_state),
	])

	useEffect(() => {
		if (!completed) return

		const timer = setTimeout(() => setCompleted(false), 2000)

		return () => clearTimeout(timer)
	}, [completed])

	const saveChanges = () => {
		if (has_changes) {
			saveOrganization()
			saveLandingPageConfig()
		}
	}

	const restoreLandingPageState = () => {
		saveLandingPageConfig({ variables: { id: selected, reset_values: true } })
	}

	const toggleSection = (new_select) => {
		setSectionOpen(new_select === section_open ? null : new_select)
	}

	const loading =
		saving_organization || saving_landing_page_config || fetching_organization || fetching_landing_page_config

	const is_not_defualt =
		!config_state?.has_default_state ||
		organization?.social_share === false ||
		organization?.confirm_digital_registrations === true

	return (
		<Modal show modalDidClose={handleClose} style={{ width: 500 }}>
			{loading && (
				<FontAwesomeIcon
					className="float-right text-primary"
					icon="spinner"
					spin
					style={{ fontSize: '25px' }}
				/>
			)}

			<h3>Landing Page Preferences</h3>
			<p>
				Customize the fields that appear on the registration form along with other registration related
				settings.
			</p>

			<Header className="mb-3">
				<span>Campaign Type:</span>

				<DropDownMenu
					Button={
						<span className="ml-2">
							{landing_page_config?.objective_to_s}{' '}
							<FontAwesomeIcon icon="caret-down" style={{ marginBottom: '-2px', marginLeft: '3px' }} />
						</span>
					}
				>
					<ul className="list-unstyled">
						{organization?.landing_page_configs?.map((config) => (
							<li key={config.id} onClick={() => setSelected(config.id)}>
								{config.objective_to_s}
							</li>
						))}
					</ul>
				</DropDownMenu>
			</Header>

			{is_not_defualt && (
				<Header className="mb-3 align-items-start">
					<FontAwesomeIcon icon="exclamation-triangle" className="text-warning mt-2 mr-3" size="lg" />

					<div className="d-flex align-items-end flex-column">
						<span style={{ fontSize: '1rem' }}>
							You made changes to the Landing Page preferences for this type of Campaign. This means that
							users in your Organization will not be able to modify their own Landing Page preferences. If
							you want users to make changes, you can switch back to the default configuration using the
							button below.
						</span>

						<button onClick={restoreLandingPageState} className="btn btn-primary btn-sm" disabled={loading}>
							Set Default Values
						</button>
					</div>
				</Header>
			)}

			<Section
				title="User Options"
				icon="cogs"
				toggleSection={() => toggleSection('options')}
				is_open={section_open === 'options'}
			>
				<OrganizationOptions state={organization_state} mergeState={mergeOrganziationState} />
			</Section>

			<Section
				title="Registration"
				icon="user-check"
				toggleSection={() => toggleSection('registartion')}
				is_open={section_open === 'registartion'}
			>
				<ContactRegistrationFields
					state={config_state}
					handleInputChange={handleConfigInputChange}
					mergeState={mergeConfigState}
					confirm_digital_registrations={organization_state.confirm_digital_registrations}
					toggleConfirmDigitalRegistrations={() =>
						mergeOrganziationState({
							confirm_digital_registrations: !organization_state.confirm_digital_registrations,
						})
					}
					is_for_organizations
				/>
			</Section>

			<div className="float-right mt-3 d-flex align-items-center">
				<CheckIcon
					icon="check-circle"
					className="text-success"
					size="lg"
					style={{ opacity: completed ? 1 : 0 }}
				/>

				<button onClick={saveChanges} className="btn btn-success ml-3" disabled={!has_changes}>
					Save Changes
				</button>
			</div>
			<button onClick={handleClose} className="btn btn-primary mt-3">
				Close
			</button>
		</Modal>
	)
}

LandingPagePreferences.propTypes = {
	organization_id: PropTypes.string,
	handleClose: PropTypes.func,
}

export default LandingPagePreferences
